import type { JSONContent } from '@tiptap/react'
import { twMerge } from 'tailwind-merge'

import { parseJsonContent } from '@/components/common/TextArea/RichTextArea/lib'

/**
 * Empty document content for initializing editors
 */
export const BLANK_CONTENT: JSONContent = {
  type: 'doc',
  content: [{ type: 'paragraph' }],
}

/**
 * Check if editor content has meaningful text (not just empty paragraphs)
 */
export const hasEditorContent = (content: JSONContent): boolean => {
  if (!content.content?.length) return false
  if (content.content.length === 1 && content.content[0].type === 'paragraph') {
    const paragraph = content.content[0]
    if (!paragraph.content?.length) return false
    if (
      paragraph.content.length === 1 &&
      paragraph.content[0].type === 'text' &&
      !paragraph.content[0].text?.trim()
    ) {
      return false
    }
  }
  return true
}

/**
 * Direct Tailwind typography classes to replace @tailwindcss/typography's `prose` classes.
 *
 * ## Why not use `prose`?
 *
 * The `prose` class from @tailwindcss/typography generates extremely complex CSS selectors
 * that cause expensive "Recalculate style" browser tasks. This becomes a significant
 * performance problem when:
 *
 * 1. Many elements use `prose` (e.g., rendering a list of comments)
 * 2. The container is animated (e.g., framer-motion's `motion.div` or `AnimatedPanel`)
 * 3. Large documents are being edited (e.g., TipTap editor with prose styling)
 *
 * In our case, the Comments panel uses AnimatedPanel for slide-in/out animations.
 * When multiple comments were rendered with `prose` classes, every animation frame
 * triggered expensive style recalculations, causing severe lag (< 5 FPS).
 *
 * ## Solution
 *
 * Replace `prose` with direct Tailwind selectors using the `[&_element]` syntax.
 * This achieves the same visual styling but with much simpler CSS that doesn't
 * trigger expensive style recalculations.
 *
 * Example:
 * - Before: `prose prose-sm prose-p:my-0 prose-a:text-blue-600`
 * - After:  `text-sm [&_p]:my-0 [&_a]:text-blue-600`
 *
 * ## Reference
 *
 * GitHub Issue: https://github.com/tailwindlabs/tailwindcss-typography/issues/409
 *
 * ## Usage
 *
 * Import `COMMENT_TYPOGRAPHY_CLASSNAME` (pre-merged string) for use with `twMerge`:
 *
 * ```tsx
 * import { COMMENT_TYPOGRAPHY_CLASSNAME } from '@/components/pursuits/v2/dashboard/comments/lib'
 *
 * <div className={twMerge('my-class', COMMENT_TYPOGRAPHY_CLASSNAME, className)}>
 *   {content}
 * </div>
 * ```
 *
 * Used by:
 * - `CommentView` - Static rendering of comment content
 * - `CommentEditor` - TipTap editor for writing comments
 * - `useCommentEditor` hook - Editor instance configuration
 */
export const COMMENT_TYPOGRAPHY_CLASSES = [
  // Base
  'text-sm leading-relaxed',
  'text-primary-800 dark:text-primary-200',
  // Headings
  '[&_h1]:text-xl [&_h1]:font-bold [&_h1]:my-0',
  '[&_h2]:text-lg [&_h2]:font-semibold [&_h2]:my-0',
  '[&_h3]:text-base [&_h3]:font-semibold [&_h3]:my-0',
  '[&_h4]:text-sm [&_h4]:font-semibold [&_h4]:my-0',
  '[&_h5]:text-sm [&_h5]:font-medium [&_h5]:my-0',
  '[&_h6]:text-xs [&_h6]:font-medium [&_h6]:my-0',
  // Paragraphs
  '[&_p]:my-0',
  // Lists
  '[&_ul]:my-0 [&_ul]:list-disc [&_ul]:pl-4',
  '[&_ol]:my-0 [&_ol]:list-decimal [&_ol]:pl-4',
  '[&_li]:my-0 [&_li]:pl-0.5',
  '[&_li_p]:inline',
  // Nested lists
  '[&_ul_ul]:my-0 [&_ol_ol]:my-0 [&_ul_ol]:my-0 [&_ol_ul]:my-0',
  // Links
  '[&_a]:text-blue-600 dark:[&_a]:text-blue-400 [&_a]:no-underline hover:[&_a]:underline',
  // Strong/emphasis
  '[&_strong]:font-semibold [&_b]:font-semibold',
  '[&_em]:italic [&_i]:italic',
  // Strikethrough
  '[&_s]:line-through [&_del]:line-through [&_strike]:line-through',
  // Underline
  '[&_u]:underline',
  // Code (inline)
  '[&_code]:text-xs [&_code]:font-mono [&_code]:bg-primary-100 dark:[&_code]:bg-primary-800 [&_code]:text-primary-700 dark:[&_code]:text-primary-300 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded',
  // Code blocks (pre)
  '[&_pre]:my-2 [&_pre]:p-3 [&_pre]:bg-primary-100 dark:[&_pre]:bg-primary-800 [&_pre]:rounded-md [&_pre]:overflow-x-auto',
  '[&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-xs',
  // Blockquotes (matches editor's StarterKit blockquote config)
  '[&_blockquote]:my-2 [&_blockquote]:pl-4 [&_blockquote]:border-l-4 [&_blockquote]:border-primary-300 dark:[&_blockquote]:border-primary-600 [&_blockquote]:text-primary-600 dark:[&_blockquote]:text-primary-400 [&_blockquote]:italic',
  // Horizontal rule
  '[&_hr]:my-3 [&_hr]:border-primary-200 dark:[&_hr]:border-primary-700',
  // Images
  '[&_img]:max-w-full [&_img]:h-auto [&_img]:rounded',
  // Tables
  '[&_table]:w-full [&_table]:my-2 [&_table]:text-sm',
  '[&_th]:text-left [&_th]:font-semibold [&_th]:p-2 [&_th]:border-b [&_th]:border-primary-200 dark:[&_th]:border-primary-700',
  '[&_td]:p-2 [&_td]:border-b [&_td]:border-primary-100 dark:[&_td]:border-primary-800',
  // Subscript/superscript
  '[&_sub]:text-xs [&_sup]:text-xs',
  // Mark/highlight
  '[&_mark]:bg-yellow-200 dark:[&_mark]:bg-yellow-800 [&_mark]:px-0.5 [&_mark]:rounded-sm',
] as const

export const COMMENT_TYPOGRAPHY_CLASSNAME = twMerge(
  ...COMMENT_TYPOGRAPHY_CLASSES,
)

/**
 * Validates and converts comment content to proper JSON format.
 * Falls back to markdown conversion if content is not valid JSON.
 */
export const validateAndConvertContent = (
  content: string,
): JSONContent | null => {
  // First try to parse as JSON
  const jsonContent = parseJsonContent(content)
  if (jsonContent) {
    return jsonContent
  }

  // If not JSON, treat as markdown and convert to HTML, then create simple JSON structure
  try {
    // Create a simple paragraph node with the HTML content
    return {
      type: 'doc',
      content: [
        {
          type: 'paragraph',
          content: [
            {
              type: 'text',
              text: content.trim(), // Use original markdown text for now
            },
          ],
        },
      ],
    }
  } catch (error) {
    console.error('Failed to convert markdown content:', error)

    // Last resort: create a simple text node
    return {
      type: 'doc',
      content: [
        {
          type: 'paragraph',
          content: [
            {
              type: 'text',
              text: content,
            },
          ],
        },
      ],
    }
  }
}

/**
 * Checks if content is valid JSON
 */
export const isJsonContent = (content: string): boolean => {
  try {
    JSON.parse(content)
    return true
  } catch {
    return false
  }
}

/**
 * Converts TipTap JSON content to string for API submission
 */
export const contentToString = (content: JSONContent): string => {
  return JSON.stringify(content)
}
